import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { Box, Button, Chip, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import { ListAltOutlined as EditIcon, DeleteOutlined as DeleteIcon, Add } from '@mui/icons-material';
import { ConfigurationAPI } from '../apis';
import { CONFIGURATION_TYPES, CONFIGURATION_TYPE_LABELS } from '../constants/configuration';
import { constants } from '../constants';
import { AddConfigurationModal } from '../components/Configuration';
import { ConfirmationDialog } from '../components';
const ConfigurationPage = () => {
    const [configurations, setConfigurations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedType, setSelectedType] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedConfiguration, setSelectedConfiguration] = useState(null);
    const [selectedItemIndex, setSelectedItemIndex] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [configurationToDelete, setConfigurationToDelete] = useState(null);
    const [itemIndexToDelete, setItemIndexToDelete] = useState(null);
    // Load configurations on component mount and when type filter changes
    useEffect(() => {
        loadConfigurations();
    }, [selectedType]);
    const loadConfigurations = async () => {
        try {
            setLoading(true);
            const response = await ConfigurationAPI.getConfigurations(selectedType || undefined);
            setConfigurations(response.resource || []);
        }
        catch {
            setConfigurations([]);
        }
        finally {
            setLoading(false);
        }
    };
    const handleAddConfiguration = () => {
        setSelectedConfiguration(null); // Reset for add mode
        setSelectedItemIndex(null);
        setModalOpen(true);
    };
    const handleEditConfiguration = (configuration, itemIndex) => {
        setSelectedConfiguration(configuration);
        setSelectedItemIndex(itemIndex);
        setModalOpen(true);
    };
    const handleDeleteConfiguration = (configuration, itemIndex) => {
        setConfigurationToDelete(configuration);
        setItemIndexToDelete(itemIndex);
        setDeleteDialogOpen(true);
    };
    const confirmDeleteConfiguration = async () => {
        if (!configurationToDelete || itemIndexToDelete === null)
            return;
        try {
            // If only one item, delete entire configuration
            if (configurationToDelete.items.length === 1) {
                await ConfigurationAPI.deleteConfiguration(configurationToDelete.id);
            }
            else {
                // Remove the specific item and update configuration
                const updatedItems = configurationToDelete.items.filter((_, index) => index !== itemIndexToDelete);
                await ConfigurationAPI.updateConfiguration(configurationToDelete.id, {
                    resourceType: 'Configuration',
                    type: configurationToDelete.type,
                    items: updatedItems,
                    details: configurationToDelete.details || {}
                });
            }
            await loadConfigurations(); // Reload the list
            setDeleteDialogOpen(false);
            setConfigurationToDelete(null);
            setItemIndexToDelete(null);
        }
        catch {
            // Error handled silently - user can retry
        }
    };
    const handleConfigurationSaved = () => {
        // Reload configurations after add/edit
        loadConfigurations();
        setModalOpen(false);
        setSelectedConfiguration(null);
        setSelectedItemIndex(null);
    };
    const getTypeChipColor = (type) => {
        switch (type) {
            case CONFIGURATION_TYPES.SYSTEM:
                return 'secondary';
            case CONFIGURATION_TYPES.USER_EXPERIENCE:
                return 'primary';
            default:
                return 'default';
        }
    };
    const getValueTypeLabel = (value) => {
        if (typeof value === 'string') {
            return constants.configuration_value_type_text;
        }
        if (typeof value === 'number') {
            return constants.configuration_value_type_number;
        }
        if (typeof value === 'boolean') {
            return constants.configuration_value_type_boolean;
        }
        return constants.configuration_value_type_json_object;
    };
    const renderLoadingRow = () => (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 4, sx: { textAlign: 'center', py: 4 }, children: constants.configuration_loading }) }));
    const renderEmptyRow = () => (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 4, sx: { textAlign: 'center', py: 4 }, children: constants.configuration_empty }) }));
    const renderEditAction = (config, itemIndex) => (_jsx(IconButton, { onClick: () => handleEditConfiguration(config, itemIndex), sx: {
            color: '#26890d',
            '&:hover': {
                bgcolor: 'rgba(38, 137, 13, 0.04)'
            }
        }, children: _jsx(EditIcon, {}) }));
    const renderDeleteAction = (config, itemIndex) => (_jsx(IconButton, { onClick: () => handleDeleteConfiguration(config, itemIndex), sx: {
            color: '#da291c',
            '&:hover': {
                bgcolor: 'rgba(218, 41, 28, 0.04)'
            }
        }, children: _jsx(DeleteIcon, {}) }));
    const renderTableBody = () => {
        if (loading) {
            return renderLoadingRow();
        }
        if (configurations.length === 0) {
            return renderEmptyRow();
        }
        return configurations.flatMap((config) => config.items.map((item, index) => (_jsxs(TableRow, { sx: {
                '&:hover': {
                    backgroundColor: '#f5f5f5'
                },
                borderBottom: '1px solid #ebebeb'
            }, children: [_jsxs(TableCell, { sx: {
                        borderBottom: '1px solid #ebebeb',
                        py: 2
                    }, children: [item.key, index === 0 && (_jsx(Chip, { label: CONFIGURATION_TYPE_LABELS[config.type], size: "small", color: getTypeChipColor(config.type), sx: {
                                ml: 1,
                                fontSize: '11px'
                            } }))] }), _jsx(TableCell, { sx: {
                        borderBottom: '1px solid #ebebeb',
                        py: 2
                    }, children: typeof item.value === 'object'
                        ? JSON.stringify(item.value)
                        : String(item.value) }), _jsx(TableCell, { sx: {
                        borderBottom: '1px solid #ebebeb',
                        py: 2
                    }, children: getValueTypeLabel(item.value) }), _jsx(TableCell, { sx: {
                        borderBottom: '1px solid #ebebeb',
                        py: 2
                    }, children: _jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [renderEditAction(config, index), renderDeleteAction(config, index)] }) })] }, `${config.id}-${index}`))));
    };
    return (_jsxs(Box, { sx: { p: 3 }, children: [_jsxs(Box, { sx: {
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }, children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: _jsxs(TextField, { select: true, label: constants.configuration_type_system, value: selectedType, onChange: (e) => setSelectedType(e.target.value), sx: { minWidth: 200 }, size: "small", children: [_jsx(MenuItem, { value: "", children: constants.configuration_filter_all_types }), Object.entries(CONFIGURATION_TYPE_LABELS).map(([typeKey, label]) => (_jsx(MenuItem, { value: typeKey, children: label }, typeKey)))] }) }), _jsx(Button, { variant: "contained", startIcon: _jsx(Add, {}), onClick: handleAddConfiguration, sx: {
                            backgroundColor: '#26890D',
                            '&:hover': {
                                backgroundColor: '#1e6b0a'
                            }
                        }, children: constants.configuration_add_button })] }), _jsx(TableContainer, { component: Paper, sx: {
                    borderRadius: 2,
                    border: '1px solid #ebebeb',
                    width: '100%',
                    overflowX: 'auto',
                    boxSizing: 'border-box',
                    minWidth: 600
                }, children: _jsxs(Table, { sx: { minWidth: 800, tableLayout: 'auto' }, children: [_jsx(TableHead, { children: _jsxs(TableRow, { sx: { backgroundColor: '#fff' }, children: [_jsx(TableCell, { sx: {
                                            fontWeight: 600,
                                            borderBottom: '1px solid #ebebeb',
                                            color: '#212121',
                                            whiteSpace: 'nowrap',
                                            minWidth: 320,
                                            maxWidth: 400,
                                            wordBreak: 'break-all'
                                        }, children: constants.configuration_table_header_key }), _jsx(TableCell, { sx: {
                                            fontWeight: 600,
                                            borderBottom: '1px solid #ebebeb',
                                            color: '#212121',
                                            minWidth: 300,
                                            wordBreak: 'break-all',
                                            whiteSpace: 'pre-line'
                                        }, children: constants.configuration_table_header_value }), _jsx(TableCell, { sx: {
                                            fontWeight: 600,
                                            borderBottom: '1px solid #ebebeb',
                                            color: '#212121',
                                            minWidth: 160,
                                            whiteSpace: 'nowrap'
                                        }, children: constants.configuration_table_header_type_of_value }), _jsx(TableCell, { sx: {
                                            fontWeight: 600,
                                            borderBottom: '1px solid #ebebeb',
                                            width: 120,
                                            color: '#212121',
                                            whiteSpace: 'nowrap'
                                        }, children: constants.configuration_table_header_actions })] }) }), _jsx(TableBody, { children: renderTableBody() })] }) }), _jsx(AddConfigurationModal, { open: modalOpen, onClose: () => {
                    setModalOpen(false);
                    setSelectedConfiguration(null);
                    setSelectedItemIndex(null);
                }, onSaved: handleConfigurationSaved, configuration: selectedConfiguration, itemIndex: selectedItemIndex, existingConfigurations: configurations }), _jsx(ConfirmationDialog, { isOpen: deleteDialogOpen, bodyText: constants.configuration_delete_confirm_body(configurationToDelete && itemIndexToDelete !== null
                    ? configurationToDelete.items[itemIndexToDelete]?.key ||
                        ''
                    : ''), submitButtonText: constants.delete, actionType: "delete", submitFunction: confirmDeleteConfiguration, setIsOpen: setDeleteDialogOpen })] }));
};
export default ConfigurationPage;
